<template>
  <div class="chat-panel">
    <!-- 主對話區 -->
    <div class="chat-main">
      <!-- Header -->
      <div class="chat-header">
        <div class="chat-header-left">
          <div class="chat-header-avatar">
            <i class="el-icon-user"></i>
          </div>
          <div>
            <div class="chat-header-name">{{ displayName }}</div>
            <div class="chat-header-sub">
              <span v-if="readonly" class="listen-mode">旁聽模式</span>
              {{ headerSubtitle }}
            </div>
          </div>
        </div>
        <div v-if="!readonly" class="chat-header-actions">
          <span v-if="transferPending" class="transfer-waiting">等待對方接受轉接…</span>
          <el-button v-if="currentSession && currentSession.customerId" size="small" icon="el-icon-user" @click="openCustomerProfile">客戶資料</el-button>`n          <el-button size="small" icon="el-icon-document-add" @click="handleCreateTicket">建立工單</el-button>
          <el-button size="small" :type="qrPanelOpen ? 'primary' : ''" icon="el-icon-document-copy" @click="qrPanelOpen = !qrPanelOpen">
            常用語
          </el-button>
          <el-button size="small" icon="el-icon-sort" :disabled="!!transferPending" @click="openTransfer">轉接</el-button>
          <el-button size="small" type="danger" icon="el-icon-circle-close" @click="closeModalOpen = true">結束服務</el-button>
        </div>
      </div>

      <!-- 客戶資訊面板 -->
      <div v-if="currentSession && (currentSession.customerId || currentSession.clientIp)" class="client-info">
        <div class="client-info-toggle" @click="clientInfoOpen = !clientInfoOpen">
          <i class="el-icon-info"></i>
          <span class="client-info-title">客戶資訊</span>
          <span class="client-info-arrow">{{ clientInfoOpen ? '▲' : '▼' }}</span>
        </div>
        <div v-if="clientInfoOpen" class="client-info-body">
          <div class="client-info-grid">
            <div v-if="currentSession.customerId" class="client-info-item">
              <span class="ci-label">帳號</span>
              <span class="ci-value"><i class="el-icon-tickets"></i> {{ currentSession.customerId }}</span>
            </div>
            <div class="client-info-item">
              <span class="ci-label">IP</span>
              <span class="ci-value">
                <i class="el-icon-location"></i> {{ currentSession.clientIp }}
                <span v-if="currentSession.clientGeo" class="client-geo">{{ currentSession.clientGeo }}</span>
              </span>
            </div>
            <div class="client-info-item">
              <span class="ci-label">來源</span>
              <span class="ci-value">
                <i class="el-icon-connection"></i>
                <el-tag size="small" :type="tagTypeOf(srcMeta.color)">{{ srcMeta.label }}</el-tag>
              </span>
            </div>
            <div v-if="device.os" class="client-info-item">
              <span class="ci-label">設備</span>
              <span class="ci-value"><i class="el-icon-monitor"></i> {{ device.os }} / {{ device.browser }}</span>
            </div>
            <div v-if="device.resolution" class="client-info-item">
              <span class="ci-label">解析度</span>
              <span class="ci-value">{{ device.resolution }}</span>
            </div>
            <div v-if="device.language" class="client-info-item">
              <span class="ci-label">語言</span>
              <span class="ci-value">{{ device.language }}</span>
            </div>
            <div v-if="device.timezone" class="client-info-item">
              <span class="ci-label">時區</span>
              <span class="ci-value">{{ device.timezone }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- 訊息區 -->
      <div ref="messagesBox" class="chat-messages">
        <div v-for="(msg, msgIdx) in messages" :key="msg.id">
          <div v-if="shouldShowTime(msgIdx)" class="msg-time-divider">
            <span>{{ formatTime(msg.createTime) }}</span>
          </div>
          <MessageBubble
            :message="msg"
            :is-agent="msg.senderType === 2"
            :customer-last-read-msg-id="customerReadWatermark || (currentSession ? currentSession.customerLastReadMsgId : 0)"
          />
        </div>
        <div ref="messagesEnd"></div>
      </div>

      <!-- 對方正在輸入 -->
      <div v-if="typingPreview" class="typing-bar">
        <span class="typing-dots">
          <span class="typing-dot" style="animation-delay: 0s">.</span>
          <span class="typing-dot" style="animation-delay: 0.2s">.</span>
          <span class="typing-dot" style="animation-delay: 0.4s">.</span>
        </span>
        <span class="typing-text">客戶正在輸入：{{ typingPreview.length > 80 ? typingPreview.slice(0, 80) + '…' : typingPreview }}</span>
      </div>

      <!-- 輸入區 -->
      <div v-if="!readonly" class="chat-input">
        <div v-if="slashQuery && slashHits.length > 0" class="slash-hits">
          <div class="slash-hits-title">常用語（/ 搜尋）— 點選插入</div>
          <el-button
            v-for="h in slashHits"
            :key="h.id"
            class="slash-hit-btn"
            @mousedown.prevent
            @click="insertQuickReplyContent(h.content)"
          >
            <div class="slash-hit-title">{{ h.title }}</div>
            <div class="slash-hit-group">{{ h.groupName ? ('「' + h.groupName + '」') : '' }}</div>
          </el-button>
        </div>

        <div class="chat-input-row">
          <div class="chat-input-editor">
            <RichTextEditor
              ref="editor"
              :value="inputHtml"
              compact
              :quick-replies="qrTree"
              placeholder="輸入消息 (Enter 發送, Shift+Enter 換行)；輸入 /關鍵字 可搜尋常用語"
              :min-height="40"
              @input="handleInputChange"
              @submit="handleSend"
            />
          </div>
          <el-button type="primary" icon="el-icon-promotion" :loading="sending" @click="handleSend">發送</el-button>
        </div>
      </div>
    </div>

    <!-- 常用語側欄 -->
    <div v-if="!readonly && qrPanelOpen" class="qr-panel">
      <div class="qr-panel-header">常用語</div>
      <div class="qr-panel-body">
        <div v-if="qrTree.length === 0" class="qr-empty">尚無常用語，請至「我的用語」頁面新增。</div>
        <el-collapse v-else class="qr-collapse">
          <el-collapse-item v-for="g in qrTree" :key="g.id" :title="g.name + ' (' + (g.replies ? g.replies.length : 0) + ')'" :name="String(g.id)">
            <div class="qr-list">
              <el-button
                v-for="r in (g.replies || [])"
                :key="r.id"
                size="small"
                class="qr-item-btn"
                @click="insertQuickReplyContent(r.content)"
              >
                <span class="qr-item-title">{{ r.title }}</span>
                <span class="qr-item-preview">
                  <SafeHtml :html="r.content" />
                </span>
              </el-button>
            </div>
          </el-collapse-item>
        </el-collapse>
      </div>
    </div>

    <!-- 轉接彈窗 -->
    <el-dialog
      v-if="!readonly"
      :visible.sync="transferModalOpen"
      title="轉接會話"
      width="420px"
      :close-on-click-modal="false"
    >
      <el-select v-model="targetAgentId" placeholder="選擇目標客服" style="width: 100%">
        <el-option
          v-for="a in transferAgents"
          :key="a.id"
          :label="a.nickname + ' (' + a.username + ')' + (a.onlineStatus === 2 ? ' · 忙碌' : '')"
          :value="a.id"
        />
      </el-select>
      <div slot="footer">
        <el-button @click="transferModalOpen = false">取消</el-button>
        <el-button type="primary" :disabled="!targetAgentId" @click="handleTransfer">確定</el-button>
      </div>
    </el-dialog>

    <!-- 結束服務彈窗 -->
    <el-dialog
      v-if="!readonly"
      :visible.sync="closeModalOpen"
      title="結束服務"
      width="420px"
      :close-on-click-modal="false"
      @close="closeCategory = null"
    >
      <span>請選擇對話分類：</span>
      <el-radio-group v-model="closeCategory" class="close-radio-group">
        <el-radio label="no_conversation">無對話</el-radio>
        <el-radio label="valid_conversation">有效對話</el-radio>
      </el-radio-group>
      <div slot="footer">
        <el-button @click="closeModalOpen = false; closeCategory = null">取消</el-button>
        <el-button type="danger" :disabled="!closeCategory" @click="handleClose">確認結束</el-button>
      </div>
    </el-dialog>
    <!-- 客戶資料彈窗 -->
    <el-dialog :visible.sync="customerProfileVisible" title="客戶資料" width="720px" append-to-body>
      <div v-loading="profileLoading" style="min-height:120px">
        <template v-if="customerProfile">
          <el-row :gutter="12" class="cp-customer" style="margin-bottom:12px">
            <el-col :span="8" style="margin-bottom:8px"><b>帳號</b>&nbsp;{{ customerProfile.customerId }}</el-col>
            <el-col :span="8" style="margin-bottom:8px"><b>姓名</b>&nbsp;{{ (customerProfile.customer||{}).name || '—' }}</el-col>
            <el-col :span="8" style="margin-bottom:8px"><b>電郵</b>&nbsp;{{ (customerProfile.customer||{}).email || '—' }}</el-col>
            <el-col :span="8"><b>電話</b>&nbsp;{{ (customerProfile.customer||{}).phone || '—' }}</el-col>
            <el-col :span="8"><b>KYC</b>&nbsp;<el-tag :type="kycTag((customerProfile.kyc||{}).status)">{{ (customerProfile.kyc||{}).status || '—' }}</el-tag></el-col>
            <el-col :span="8"><span v-if="customerProfile.customerError" style="color:#f56c6c">{{ customerProfile.customerError }}</span></el-col>
          </el-row>
          <el-tabs v-model="cpTab">
            <el-tab-pane label="入金記錄" name="deposits">
              <el-table :data="customerProfile.deposits || []" size="small" border>
                <el-table-column prop="pno" label="單號" min-width="150"></el-table-column>
                <el-table-column prop="applyAmount" label="金額" width="100"></el-table-column>
                <el-table-column prop="applyCurrency" label="幣種" width="80"></el-table-column>
                <el-table-column prop="proposalStatus" label="狀態" width="90"><template slot-scope="s"><el-tag size="mini">{{ s.row.proposalStatus }}</el-tag></template></el-table-column>
                <el-table-column prop="createDate" label="時間" min-width="150"></el-table-column>
              </el-table>
            </el-tab-pane>
            <el-tab-pane label="出金記錄" name="withdraws">
              <el-table :data="customerProfile.withdraws || []" size="small" border>
                <el-table-column prop="id" label="訂單" width="90"></el-table-column>
                <el-table-column prop="amount" label="金額" width="100"></el-table-column>
                <el-table-column prop="currency" label="幣種" width="80"></el-table-column>
                <el-table-column prop="method" label="方式" min-width="90"></el-table-column>
                <el-table-column prop="status" label="狀態" width="110"></el-table-column>
                <el-table-column prop="createdAt" label="時間" min-width="150"></el-table-column>
              </el-table>
            </el-tab-pane>
          </el-tabs>
        </template>
        <el-empty v-else-if="!profileLoading" description="無資料"></el-empty>
        <span v-else>請稍候…</span>
      </div>
    </el-dialog>  </div>
</template>

<script>
import MessageBubble from './MessageBubble.vue'
import RichTextEditor from './RichTextEditor.vue'
import SafeHtml from './SafeHtml.vue'
import {
  getSessionMessages,
  sendAgentMessage,
  closeAgentSession,
  getTransferableAgents,
  transferSession,
  timeoutTransfer,
  markSessionRead
} from '@/api/customerService/agent'
import { createTicket } from '@/api/customerService/ticket'
import { listQuickReplyTree, searchQuickReplies } from '@/api/customerService/quickReply'
import { getCustomerProfile } from '@/api/customerService/customer'

const ClientSourceMap = {
  OTHER: { label: '其他', color: '#909399' },
  web: { label: 'Web', color: '#409eff' },
  mini_program: { label: '小程序', color: '#67c23a' },
  app: { label: 'App', color: '#e6a23c' },
  wechat: { label: '微信', color: '#67c23a' },
  phone: { label: '電話', color: '#f56c6c' }
}
const COLOR_TAG_TYPE = {
  '#409eff': '',
  '#67c23a': 'success',
  '#e6a23c': 'warning',
  '#f56c6c': 'danger',
  '#909399': 'info'
}

export default {
  name: 'ChatPanel',
  components: { MessageBubble, RichTextEditor, SafeHtml },
  props: {
    sessionId: { type: [Number, String], default: null },
    agentId: { type: [Number, String], default: null },
    agentName: { type: String, default: '' },
    onClose: { type: Function, default: null },
    readonly: { type: Boolean, default: false },
    sessionMeta: { type: Object, default: null },
    messages: { type: Array, default: () => [] },
    typingPreview: { type: String, default: '' },
    customerReadWatermark: { type: [Number, String], default: 0 },
    currentSession: { type: Object, default: null }
  },
  data() {
    return {
      customerProfileVisible: false,
      customerProfile: null,
      profileLoading: false,
      cpTab: 'deposits',
      inputHtml: '',
      sending: false,
      qrPanelOpen: false,
      qrTree: [],
      slashHits: [],
      slashQuery: null,
      transferModalOpen: false,
      transferAgents: [],
      targetAgentId: null,
      transferPending: null,
      clientInfoOpen: true,
      closeModalOpen: false,
      closeCategory: null,
      outgoingTransferId: null
    }
  },
  computed: {
    displayName() {
      if (this.sessionMeta && this.sessionMeta.customerName) return this.sessionMeta.customerName
      if (this.currentSession && this.currentSession.customerName) return this.currentSession.customerName
      return '會話 ' + this.sessionId
    },
    displayChannel() {
      if (this.sessionMeta && this.sessionMeta.channel) return this.sessionMeta.channel
      if (this.currentSession && this.currentSession.channel) return this.currentSession.channel
      return ''
    },
    headerSubtitle() {
      if (this.sessionMeta && this.sessionMeta.subtitle) return this.sessionMeta.subtitle
      return this.displayChannel + ' · 會話 #' + this.sessionId
    },
    device() {
      try {
        if (this.currentSession && this.currentSession.deviceInfo) {
          return JSON.parse(this.currentSession.deviceInfo)
        }
        return {}
      } catch (e) {
        return {}
      }
    },
    srcMeta() {
      const key = (this.currentSession && this.currentSession.clientSource) || ''
      return ClientSourceMap[key] || ClientSourceMap.OTHER
    }
  },
  watch: {
    sessionId: {
      handler() {
        this.onSessionChange()
      },
      immediate: true
    },
    messages() {
      this.onMessagesChange()
    },
    transferPending(val) {
      if (this.transferTimer) clearTimeout(this.transferTimer)
      this.transferTimer = null
      if (!val) return
      this.transferTimer = setTimeout(() => {
        timeoutTransfer(val.transferId)
          .catch(() => { /* ignore */ })
          .then(() => {
            this.transferPending = null
            this.$message.warning('轉接超時')
          })
      }, 30000)
    }
  },
  created() {
    this.slashTimer = null
    this.transferTimer = null
    this.loadQuickReplies()
  },
  beforeDestroy() {
    if (this.slashTimer) clearTimeout(this.slashTimer)
    if (this.transferTimer) clearTimeout(this.transferTimer)
  },
  methods: {
    onSessionChange() {
      if (!this.sessionId) return
      getSessionMessages(this.sessionId)
        .then(res => {
          this.$emit('history-loaded', this.sessionId, this.sortMessages(res.result || []))
        })
        .catch(() => { /* ignore */ })
    },
    onMessagesChange() {
      if (this.$refs.messagesEnd) this.$refs.messagesEnd.scrollIntoView({ behavior: 'smooth' })
      if (this.readonly || !this.agentId || !this.messages || this.messages.length === 0) return
      const customerMsgs = this.messages.filter(m => m.senderType === 0)
      if (customerMsgs.length === 0) return
      markSessionRead(this.agentId, this.sessionId, customerMsgs[customerMsgs.length - 1].id)
        .catch(() => { /* ignore */ })
    },
    loadQuickReplies() {
      if (this.readonly || !this.agentId) return
      listQuickReplyTree(this.agentId)
        .then(res => {
          this.qrTree = res.result || []
        })
        .catch(() => {
          this.qrTree = []
        })
    },
    htmlToPlainText(html) {
      const div = document.createElement('div')
      div.innerHTML = html || ''
      return div.textContent || ''
    },
    isEditorEmpty(html) {
      return !this.htmlToPlainText(html).trim()
    },
    extractSlashFromText(plainText) {
      const lastNl = plainText.lastIndexOf('\n')
      const lastLine = lastNl === -1 ? plainText : plainText.slice(lastNl + 1)
      if (lastLine.startsWith('/') && lastLine.length > 1) return lastLine.slice(1)
      return null
    },
    sortMessages(msgs) {
      return msgs.slice().sort((a, b) => {
        const ta = new Date(a.createTime).getTime()
        const tb = new Date(b.createTime).getTime()
        if (ta !== tb) return ta - tb
        return (a.id || 0) - (b.id || 0)
      })
    },
    handleInputChange(html) {
      this.inputHtml = html
      this.slashQuery = this.extractSlashFromText(this.htmlToPlainText(html))
      this.debounceSlashSearch()
    },
    debounceSlashSearch() {
      if (this.slashTimer) clearTimeout(this.slashTimer)
      if (!this.slashQuery || !this.agentId || this.readonly) {
        this.slashHits = []
        return
      }
      const q = this.slashQuery
      const aid = this.agentId
      this.slashTimer = setTimeout(() => {
        searchQuickReplies(aid, q)
          .then(res => {
            this.slashHits = res.result || []
          })
          .catch(() => {
            this.slashHits = []
          })
      }, 280)
    },
    insertQuickReplyContent(htmlContent) {
      if (this.$refs.editor) {
        this.$refs.editor.setContent(htmlContent)
      } else {
        this.inputHtml = htmlContent
      }
      this.slashHits = []
      this.slashQuery = null
    },
    formatTime(ts) {
      if (!ts) return ''
      let date = new Date(ts)
      if (isNaN(date.getTime())) date = new Date()
      const hh = String(date.getHours()).padStart(2, '0')
      const mm = String(date.getMinutes()).padStart(2, '0')
      return hh + ':' + mm
    },
    shouldShowTime(idx) {
      if (idx === 0) return true
      const prev = new Date(this.messages[idx - 1].createTime).getTime()
      const curr = new Date(this.messages[idx].createTime).getTime()
      return (curr - prev) / 60000 >= 5
    },
    tagTypeOf(color) {
      return COLOR_TAG_TYPE[color] || ''
    },
    async handleSend() {
      if (this.isEditorEmpty(this.inputHtml) || this.sending || this.readonly) return
      this.sending = true
      try {
        const res = await sendAgentMessage(this.agentId, this.sessionId, { content: this.inputHtml, msgType: 0 })
        const msg = res.result
        this.$emit('append-message', msg)
        this.inputHtml = ''
        if (this.$refs.editor) this.$refs.editor.clear()
      } catch (e) {
        this.$message.error(e.message || '發送失敗')
      } finally {
        this.sending = false
      }
    },
    openCustomerProfile() {
      if (!this.currentSession || !this.currentSession.customerId) return
      this.customerProfileVisible = true
      this.loadCustomerProfile()  },
    kycTag(st) {
      if (st === 'APPROVED') return 'success'
      if (st === 'REJECTED') return 'danger'
      if (st === 'PENDING') return 'warning'
      return 'info'
    },
    async loadCustomerProfile() {
      const cid = this.currentSession && this.currentSession.customerId
      if (!cid) return
      this.profileLoading = true
      try {
        const res = await getCustomerProfile(cid)
        this.customerProfile = res.result
        const d = res.result && res.result.deposits
        this.cpTab = (d && d.length) ? 'deposits' : 'withdraws'
      } catch (e) {
        this.$message.error((e && e.message) || '查詢客戶資料失敗')
      } finally {
        this.profileLoading = false
      }
    },
    async handleClose() {
      if (this.readonly || !this.closeCategory) return
      try {
        await closeAgentSession(this.agentId, this.sessionId, this.closeCategory)
        this.$message.success('會話已結束')
        if (this.onClose) this.onClose()
      } catch (e) {
        this.$message.error(e.message || '操作失敗')
      } finally {
        this.closeModalOpen = false
        this.closeCategory = null
      }
    },
    async openTransfer() {
      if (this.readonly || this.transferPending) return
      try {
        const res = await getTransferableAgents(this.agentId)
        this.transferAgents = res.result || []
        this.transferModalOpen = true
      } catch (e) {
        this.$message.error(e.message || '載入可轉接客服失敗')
      }
    },
    async handleTransfer() {
      if (!this.targetAgentId || this.readonly) return
      try {
        const res = await transferSession(this.agentId, this.sessionId, this.targetAgentId)
        const transferId = (res.result && res.result.transferId) || null
        this.outgoingTransferId = transferId
        this.transferPending = { transferId: transferId, sessionId: this.sessionId }
        this.transferModalOpen = false
        this.targetAgentId = null
        this.$message.info('已發送轉接請求，等待對方接受…')
      } catch (e) {
        this.$message.error(e.message || '轉接失敗')
      }
    },
    async handleCreateTicket() {
      if (this.readonly) return
      const cs = this.currentSession || {}
      try {
        await createTicket({
          sessionId: this.sessionId,
          customerId: cs.customerId,
          customerName: cs.customerName,
          title: '來自會話 ' + this.sessionId + ' 的工單',
          content: '',
          assigneeId: this.agentId
        })
        this.$message.success('工單已建立')
      } catch (e) {
        this.$message.error(e.message || '建立失敗')
      }
    }
  }
}
</script>

<style scoped>
.chat-panel {
  height: 100%;
  display: flex;
  flex-direction: row;
  min-height: 0;
}
.chat-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.chat-header {
  padding: 12px 20px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8f9fb;
}
.chat-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.chat-header-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #e0e7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4f46e5;
}
.chat-header-name {
  font-size: 15px;
  font-weight: 600;
}
.chat-header-sub {
  font-size: 12px;
  color: #909399;
}
.listen-mode {
  color: #e6a23c;
  margin-right: 8px;
}
.chat-header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.transfer-waiting {
  font-size: 12px;
  color: #909399;
}
.client-info {
  border-bottom: 1px solid #ebeef5;
  background: #f5f7fa;
  font-size: 12px;
}
.client-info-toggle {
  padding: 6px 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  color: #909399;
  user-select: none;
}
.client-info-title {
  font-weight: 500;
}
.client-info-arrow {
  margin-left: auto;
}
.client-info-body {
  padding: 0 20px 10px;
}
.client-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px 16px;
}
.client-info-item {
  min-width: 0;
}
.ci-label {
  color: #909399;
  margin-right: 8px;
}
.ci-value {
  word-break: break-word;
}
.desc-icon {
  margin-right: 4px;
  vertical-align: -2px;
}
.client-geo {
  margin-left: 6px;
  color: #909399;
}
.chat-messages {
  flex: 1;
  overflow: auto;
  padding: 16px 20px;
  background: #f7f8fa;
}
.msg-time-divider {
  text-align: center;
  margin: 12px 0;
}
.msg-time-divider span {
  font-size: 12px;
  color: #c0c4cc;
  background: rgba(0, 0, 0, 0.04);
  padding: 2px 12px;
  border-radius: 10px;
}
.typing-bar {
  padding: 6px 20px;
  background: #f5f7fa;
  border-top: 1px solid #ebeef5;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #909399;
  font-style: italic;
  overflow: hidden;
}
.typing-dots {
  flex-shrink: 0;
}
.typing-dot {
  display: inline-block;
  animation: typingDot 1.4s infinite;
}
@keyframes typingDot {
  0%, 60%, 100% { opacity: 0.2; }
  30% { opacity: 1; }
}
.typing-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chat-input {
  padding: 8px 12px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}
.slash-hits {
  margin-bottom: 8px;
  max-height: 180px;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: #fafafa;
  padding: 4px;
}
.slash-hits-title {
  font-size: 12px;
  color: #909399;
  padding: 4px 8px;
  display: block;
}
.slash-hit-btn {
  display: block;
  width: 100%;
  height: auto;
  text-align: left;
  padding: 6px 10px;
}
.slash-hit-title {
  font-weight: 600;
}
.slash-hit-group {
  font-size: 12px;
  color: #909399;
}
.chat-input-row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}
.chat-input-editor {
  flex: 1;
}
.qr-panel {
  width: 260px;
  flex-shrink: 0;
  border-left: 1px solid #ebeef5;
  background: #f5f7fa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.qr-panel-header {
  padding: 10px 12px;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
  font-size: 13px;
}
.qr-panel-body {
  flex: 1;
  overflow: auto;
  padding: 8px;
}
.qr-empty {
  font-size: 12px;
  color: #909399;
}
.qr-collapse {
  border: none;
}
.qr-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.qr-item-btn {
  display: block;
  width: 100%;
  height: auto;
  text-align: left;
  white-space: normal;
}
.qr-item-title {
  display: block;
  font-weight: 600;
}
.qr-item-preview {
  display: block;
  max-height: 48px;
  overflow: hidden;
  font-size: 11px;
  color: #909399;
}
.close-radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
}
</style>